import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Actions
import { VollmachtRequestFormAction } from "../../../store/actions/vorgange/VollmachtRequestFormAction";

// Core Components
import MDCButton from "../../core/MDCButton";
import MDCModal from "../../core/MDCModal";

// Icons
import carLogo from "../../../assets/images/my_digital_car.svg";

// Material UI
import Grid from "@mui/material/Grid";
import MDCLoader from "../../core/MDCLoader";
import MDCText from "../../core/MDCText";
import MDCLabel from "../../core/MDCLabel";
import ErrorText from "../../shared/ErrorText";
import DiscardIcon from "../../iconComponents/DiscardIcon";
import AddIconLarge from "../../iconComponents/AddIconLarge";
import MDCDocumentUpload from "../../shared/MDCDocumentUpload";

// Theme
import theme from "../../../themes/mdc-theme";

// Constants
import { EMAIL_ROW_COLUMNS } from "../../../constants/ManualRequestFormConstants";

// Styles
const useStyles = makeStyles((theme) => ({
  headerCss: {
    padding: "2rem",
    alignItems: "center",
  },
  headerColor: { color: theme.palette.MDCColors.black },
  centerLoader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  addIconBackground: {
    background: "white !important",
    "&& .css-1gnd1fd-MuiButton-endIcon": { margin: "0rem" },
    "&.css-1yk1zgn-MuiButtonBase-root-MuiButton-root": {
      minWidth: "0px !important",
      maxWidth: "45px !important",
    },
  },
  discardIconPointer: {
    cursor: "pointer",
  },
  addIconPointer: {
    cursor: "pointer",
    marginTop: "-0.5rem",
  },
  shareholder: {
    backgroundColor: "#CBEFCD",
    padding: "5px",
    textAlign: "center",
    fontSize: "0.875rem",
  },
  fleetManager: {
    backgroundColor: "#F9C7CF",
    padding: "5px",
    textAlign: "center",
    fontSize: "0.875rem",
  },
  emailIndexRowError: {
    marginTop: "-3rem",
  },
  emailRowList: {
    marginTop: "3rem",
    marginBottom: "1rem",
    color: theme.palette.MDCColors.textColor,
  },
}));

const SignatureSendEmailPopupJP = ({
  show,
  handlePopupClose,
  requestId,
  vollmatchtType,
  halterType,
}) => {
  const formFields = {
    vorname: "",
    nachname: "",
    emailadresse: "",
    bvVorname: "",
    bvNachname: "",
    bvEmailadresse: "",
  };

  const [contactEmail, setContactEmail] = useState("");
  const [hraHrbNummer, setHraHrbNummer] = useState("");
  const [rows, setRows] = useState([formFields]);
  const [errors, setErrors] = useState({});

  const [fileName, setFileName] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const {
    headerCss,
    headerColor,
    centerLoader,
    addIconBackground,
    discardIconPointer,
    addIconPointer,
    fleetManager,
    shareholder,
    emailIndexRowError,
    emailRowList,
  } = useStyles();

  const addRow = () => {
    setRows([...rows, formFields]);
  };

  const deleteRow = (id) => {
    setRows(rows.filter((row, index) => index !== id));
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(newErrors).map((errorKey) => {
        // deleting errors of that particular row based on index
        if (errorKey.includes(id)) delete newErrors[errorKey];
      });
      return newErrors;
    });
  };

  const handleChange = (id, field, value) => {
    setRows(
      rows.map((row, index) =>
        index === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleFileClick = (event) => {
    setUploadedFile(event.target.value);
    setFileName(event.target.name);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Now sending data at Back-End
    setLoading(true);
    const requestData = {
      contactEmail,
      hraHrbNummer,
      additionalFile: {
        fileName: fileName,
        content: uploadedFile,
      },
      verpflichtendeUnterzeichner: rows,
      halterType: halterType,
    };
    dispatch(
      VollmachtRequestFormAction.sendSignatureEmailDetails(
        vollmatchtType,
        requestId,
        requestData
      )
    ).then((resp) => {
      if (resp?.status === 200) {
        toast.success(resp?.data?.message);
        handlePopupClose();
      } else if (resp?.response?.status === 400) {
        const { errorFieldMap } = resp?.response?.data;
        // Setting error Validations came from Back-end
        setErrors(errorFieldMap);
      }
      setLoading(false);
    });
  };

  const getModalHeader = () => {
    return (
      <Grid container className={headerCss}>
        <Grid item xs={9} className={headerColor}>
          <h4>Externer Signaturprozess</h4>
        </Grid>
        <Grid item xs={3}>
          <img alt="My Digital Car" src={carLogo} width="100%" />
        </Grid>
      </Grid>
    );
  };

  const getModalBody = () => {
    return (
      <Grid marginX={"2rem"} marginBottom={"2rem"}>
        <Grid
          container
          className={loading && centerLoader}
          minHeight={"20rem"}
          spacing={"5"}
        >
          {loading ? (
            <MDCLoader />
          ) : (
            <>
              <Grid container spacing={5}>
                <Grid item xs={"12"} md="6" mt="1.5rem">
                  <MDCLabel label="Emailadresse für Kontaktaufnahme" />
                  <MDCText
                    name="contactEmail"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                    }}
                    error={errors && errors["contactEmail"]}
                  />

                  <ErrorText error={errors && errors["contactEmail"]} />
                </Grid>
                <Grid item xs={"12"} md="6" mt="1.5rem">
                  <MDCLabel label="HRA/HRB-Nummer" />
                  <MDCText
                    name="hraHrbNummer"
                    value={hraHrbNummer}
                    onChange={(e) => {
                      setHraHrbNummer(e.target.value);
                    }}
                    error={errors && errors["hraHrbNummer"]}
                  />

                  <ErrorText error={errors && errors["hraHrbNummer"]} />
                </Grid>
              </Grid>

              <Grid item xs={"12"}>
                <p className={emailRowList}>
                  Liste der verpflichtenden Unterzeichner:
                </p>
              </Grid>

              <Grid item xs={"12"} md="6" className={shareholder}>
                <span>
                  Zeichnungsberechtigte gemäß Handelsregister (z.B.
                  Gesellschafter)
                </span>
              </Grid>

              <Grid item xs={"12"} md="6" className={fleetManager}>
                <span>
                  durch Zeichnungsberechtigte Bevollmächtigte (z.B.
                  Flottenmanager) (Optional)
                </span>
              </Grid>

              <Grid item xs={"12"} md="12">
                {rows.map((row, index) => (
                  <Grid
                    container
                    key={index}
                    spacing={0.5}
                    marginTop={"1rem"}
                    marginBottom={"1.5rem"}
                    display={"flex"}
                    justifyContent={"space-between"}
                  >
                    {EMAIL_ROW_COLUMNS.map((itemIndex, columnIndex) => (
                      <Grid
                        item
                        xs={itemIndex.size}
                        marginBottom="0.5rem"
                        key={columnIndex}
                      >
                        <MDCLabel label={itemIndex.label} isReadOnly />
                        <MDCText
                          value={row[itemIndex.id]}
                          onChange={(e) =>
                            handleChange(index, itemIndex.id, e.target.value)
                          }
                          error={
                            errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].${itemIndex.id}`
                            ]
                          }
                        />
                        <ErrorText
                          error={
                            errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].${itemIndex.id}`
                            ]
                          }
                        />
                      </Grid>
                    ))}

                    <Grid
                      item
                      xs={0.5}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={index === 0 ? "flex-end" : "center"}
                      mt={"1rem"}
                    >
                      <div
                        className={
                          (errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].vorname`
                            ]) ||
                          (errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].nachname`
                            ]) ||
                          (errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].emailadresse`
                            ]) ||
                          (errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].bvVorname`
                            ]) ||
                          (errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].bvNachname`
                            ]) ||
                          (errors &&
                            errors[
                              `verpflichtendeUnterzeichner[${index}].bvEmailadresse`
                            ])
                            ? emailIndexRowError
                            : ""
                        }
                      >
                        {index === 0 ? (
                          <div className={addIconPointer}>
                            <MDCButton
                              className={addIconBackground}
                              label=""
                              variant="contained"
                              endIcon={<AddIconLarge />}
                              onClick={addRow}
                            />
                          </div>
                        ) : (
                          <div className={discardIconPointer}>
                            <DiscardIcon
                              color={theme.palette.MDCColors.color23}
                              onClick={() => deleteRow(index)}
                            />
                          </div>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                ))}
                <Grid
                  item
                  xs={"12"}
                  md="12"
                  display={"flex"}
                  justifyContent={"flex-end"}
                >
                  <Grid>
                    <MDCDocumentUpload
                      acceptFileFormats={".zip"}
                      documentName={
                        uploadedFile
                          ? "Vollmacht für Bevollmächtigte"
                          : "Vollmacht für Bevollmächtigte hochladen"
                      }
                      uploadedFileData={uploadedFile}
                      handleChange={(e) => handleFileClick(e)}
                      error={errors && errors["additionalFile"]}
                      isShowUploadedLabel
                      isDetailDialog
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    );
  };

  const getModalFooter = () => {
    return (
      <Grid container pb={3} paddingX={"2rem"} justifyContent="space-between">
        <Grid item>
          <MDCButton
            onClick={handlePopupClose}
            variant="outlined"
            label="Schließen"
          />
        </Grid>

        <Grid item>
          <MDCButton
            onClick={handleSubmit}
            variant="contained"
            label="Anforderung senden"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <MDCModal
      width="90%"
      open={show}
      title={getModalHeader()}
      body={getModalBody()}
      footer={getModalFooter()}
      isScroll
    />
  );
};

export default SignatureSendEmailPopupJP;
